import { Suspense, lazy } from 'react';
import { Icon, Reveal, type IconName } from '@byaft/ui';
import { usePageMeta } from '@byaft/ui/seo';

// The scroll-film carries three.js + Lenis — load it on demand.
const AboutFilm = lazy(() => import('../features/film/AboutFilm'));

function FilmFallback() {
  return <div className="min-h-[100svh]" aria-hidden="true" />;
}

const principles: { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'bolt',
    title: 'Fast by default',
    body: 'Everything opens instantly and does one job well. No bloat, no spinners between you and the work.',
  },
  {
    icon: 'sparkle',
    title: 'Free and open to all',
    body: 'No paywalls on the essentials and no sign-up walls. The everyday software you need should just be there.',
  },
  {
    icon: 'check',
    title: 'Simple, not simplistic',
    body: 'Clean interfaces that respect your time — powerful enough to be useful, calm enough to stay out of your way.',
  },
];

export function About() {
  usePageMeta({
    title: 'About',
    seoTitle: 'About BYAFT — Software, built with energy.',
    seoDescription:
      'Why BYAFT exists: fast, free, browser-based software with no sign-up — and security for the AI era. What BYAFT stands for and where it is going.',
  });

  return (
    <>
      {/* Crawlable heading + intro (the hero is a non-prerendered WebGL film). */}
      <header className="sr-only">
        <h1>About BYAFT</h1>
        <p>
          BYAFT is a studio building fast, free, browser-based software, built to stay out of your
          way — and expanding into security for the AI era. Here is why it exists, what it stands
          for, and where it is going.
        </p>
      </header>

      <Suspense fallback={<FilmFallback />}>
        <AboutFilm />
      </Suspense>

      {/* Principles — unique to the About page */}
      <section className="px-margin-safe pb-stack-xl pt-stack-lg" aria-labelledby="principles-heading">
        <div className="container-byaft">
          <Reveal>
            <h2 id="principles-heading" className="text-headline-xl text-on-background">
              What BYAFT stands for
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 90} className="h-full">
                <div className="flex h-full flex-col rounded-2xl border border-glass-border bg-surface-container-high p-7">
                  <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-surface text-primary-container">
                    <Icon name={p.icon} size={22} />
                  </div>
                  <h3 className="mb-2 font-display text-lg font-bold text-on-background">{p.title}</h3>
                  <p className="text-body-md text-on-surface-variant">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
